import React from 'react';

// Material UI Components
import Grid from '@material-ui/core/Grid';
import { Switch, Table, TableBody, TableCell, TableRow } from
  '@material-ui/core';

// Styling
import DocumentationStyles from '../sass/partials/_documentation.module.scss';
import classNames from 'classnames';
import NetworkCalls from '../utils/NetworkCalls';

interface fileInfo {
  url: string;
  name: string;
}

interface folderInfo {
  id: any;
  name: any;
}

interface State {
  originalFiles: fileInfo[];
  files: fileInfo[];
  fileView: boolean;
  currentFolder: string;
  folderBreadcrumbs: folderInfo[];
  parent: folderInfo;
  subDirectories: folderInfo[];
  searchValue: string;
  allFiles: fileInfo[];
}

export default class Documentation extends React.Component<any, State> {
  public state = {
    originalFiles: [],
    files: [],
    fileView: true,
    currentFolder: "",
    folderBreadcrumbs: [],
    parent: {
      id: "",
      name: ""
    },
    subDirectories: [],
    searchValue: "",
    allFiles: []
  };

  private abortController = new AbortController();

  private async fetchFiles(id: string): Promise<void> {
    try {
      const res: any = await NetworkCalls.fetchFiles(id, this.abortController);
      const file: fileInfo = {
        url: res.url,
        name: res.name
      };
      const files: fileInfo[] = this.state.files;
      files.push(file);
      files.sort((a, b) => a.name > b.name ? 1 : -1)
      this.setState({ files: files, originalFiles: files });
    } catch { }
  }

  private async fetchAllFiles(): Promise<void> {
    try {
      const files: fileInfo[] = this.state.files;
      const res: any = await NetworkCalls.fetchAllFiles(this.abortController);
      res.forEach((singleFile: any) => {
          const file: fileInfo = {
            url: singleFile.url,
            name: singleFile.name
          };
          files.push(file);
          files.sort((a, b) => a.name > b.name ? 1 : -1)
        }
      );
      this.setState({ allFiles: files });
    } catch { }
  }

  private async fetchSubdirectory(id: string): Promise<void> {
    try {
      const res: any = await NetworkCalls.fetchDirectoryFromId(id,
        this.abortController);
      const subdirectory: folderInfo = {
        id: id,
        name: res.name
      };
      const subdirectories: folderInfo[] = this.state.subDirectories;
      subdirectories.push(subdirectory);
      subdirectories.sort((a, b) => a.name > b.name ? 1 : -1)
      this.setState({ subDirectories: subdirectories });
    } catch { }
  }

  private async fetchParentDirectories(id: string): Promise<void> {
    try {
      const res: any = await NetworkCalls.fetchDirectoryFromId(id,
        this.abortController);
      const folderBreadcrumb: folderInfo = {
        id: id,
        name: res.name
      };
      const folderBreadcrumbs: folderInfo[] = this.state.folderBreadcrumbs;
      folderBreadcrumbs.push(folderBreadcrumb);
      if (res.parent !== undefined) {
        this.fetchParentDirectories(res.parent);
      }
      this.setState({ folderBreadcrumbs: folderBreadcrumbs });
      this.setState({ parent: this.state.folderBreadcrumbs[1] });
    } catch { }
  }

  private async fetchDirectory(directoryName?: string): Promise<void> {
    try {
      let res: any = [];
      directoryName !== undefined ?
        res = await NetworkCalls.fetchDirectoryFromId(directoryName,
          this.abortController) : res = await
            NetworkCalls.fetchDirectory(this.abortController, directoryName);
      this.setState({
        originalFiles: [],
        files: [],
        currentFolder: res.name,
        folderBreadcrumbs: [{
          id: "",
          name: res.name
        }],
        parent: {
          id: "",
          name: ""
        },
        subDirectories: [],
        searchValue: "",
      });
      if (res.parent !== undefined) {
        this.fetchParentDirectories(res.parent);
      }
      if (res.subdirectories !== undefined)
        res.subdirectories.map((e: any) => this.fetchSubdirectory(e));
      if (res.files !== undefined) res.files.map((e: any) => this.fetchFiles(e));
    } catch { }
  }

  private searchFunction(arr: any[], searchKey: any) {
    return arr.filter(function (obj: any) {
      return Object.keys(obj).some(function (key) {
        return obj[key].toLowerCase().includes(searchKey.toLowerCase());
      })
    });
  }

  private handleSearch(e: string) {
    let searchedData = this.searchFunction(this.state.allFiles, e);
    this.setState({ searchValue: e });
    if (e === "") {
      searchedData = this.state.originalFiles;
    }
    this.setState({
      files: searchedData.map((file: any) => ({
        url: file.url,
        name: file.name,
      }))
    });
  }

  private dirClickHandler = (directoryId: string, directoryName: string) => {
    if (directoryName !== this.state.currentFolder
      && directoryName.length > 0) {
      this.fetchDirectory(directoryId);
    }
  }

  private getBreadcrumbs(): React.ReactElement {
    return (
      <h2 className={DocumentationStyles.breacrumbContainer}>
        {this.state.folderBreadcrumbs.map((dir, i) => {
          return (
            <div key={i}>
              {(i + 1 === this.state.folderBreadcrumbs.length) ? '' :
                <i className="fas fa-chevron-right" />}
              <button
                onClick={() => this.dirClickHandler(dir['id'], dir['name'])}
                className={classNames(DocumentationStyles.breadcrumb,
                  {
                    [DocumentationStyles.currentFolder]: dir['name'] ===
                      this.state.currentFolder
                  })}>
                {dir['name']}
              </button>
            </div>
          );
        })}
      </h2>
    );
  }

  private getDocumentationHeader(): React.ReactElement {
    return (
      <div className={DocumentationStyles.documentationHeader}>
        <div className={DocumentationStyles.rowHeader}>
          <button
            onClick={() => this.dirClickHandler(this.state.parent['id'],
              this.state.parent['name'])}
            className={DocumentationStyles.backButton}
          >
            <i className="fas fa-arrow-alt-circle-left" />
          </button>
          {this.getBreadcrumbs()}
          <div className={DocumentationStyles.fileFilters}>
            <div className={DocumentationStyles.searchBar}>
              <i className={classNames("fas fa-search",
                DocumentationStyles.searchIcon)} />
              <input
                placeholder="Search for a document..."
                onChange={(e) => this.handleSearch(e.target.value)}
                className={DocumentationStyles.inputSearch}
                value={this.state.searchValue}
              />
            </div>
            <div className={DocumentationStyles.switchComponent}>
              <p>Change View: </p>
              <Switch
                checked={!this.state.fileView}
                onClick={() => this.setState
                  ({ fileView: !this.state.fileView })}
                color="primary"
              />
              {!this.state.fileView ?
                <p className={DocumentationStyles.view}>List View</p> :
                <p className={DocumentationStyles.view}>File View</p>}
            </div>
          </div>
        </div>
        <hr />
      </div>
    );
  }

  private getFolders(): React.ReactElement[] {
    return this.state.subDirectories.map((directory, i) => {
      return (
        <button
          className={DocumentationStyles.individualFolderView}
          key={i}
          onClick={() => this.dirClickHandler(directory['id'],
            directory['name'])}
        >
          <div className={DocumentationStyles.folderLink}>
            <i className="fas fa-folder" />
          </div>
          <p className={DocumentationStyles.folderName}>{directory['name']}</p>
        </button>
      );
    });
  }

  private getFileView(): React.ReactElement[] {
    return this.state.files.map((file: fileInfo, i) => {
      return (
        <div className={DocumentationStyles.individualFileView} key={i}>
          <a
            target='_blank'
            rel='noopener noreferrer'
            type='application/pdf'
            href={file.url.replace(' ', "%20").replace('\\', '/')}
            className={DocumentationStyles.fileLink}
          >
            <p className={DocumentationStyles.fileDesc}>{file.name}</p>
            <i className="fas fa-file" />
          </a>
          <p className={DocumentationStyles.fileName}>
            {file.name}
          </p>
        </div>
      );
    });
  }

  private getListView(): React.ReactElement {
    return (
      <Table aria-label="simple table">
        <TableBody>
          {this.state.files.map((file: fileInfo, i) => (
            <TableRow key={i}>
              <TableCell component="th" scope="row">
                <a
                  href={file.url.replace(' ', "%20").replace('\\', '/')}
                  target='_blank'
                  rel='noopener noreferrer'
                  type='application/pdf'
                >
                  <i className="fas fa-file" />
                  {file.name}
                </a>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  public componentDidMount(): void {
    this.fetchDirectory();
    this.fetchAllFiles();
  }

  public componentWillUnmount(): void {
    this.abortController.abort();
  }

  public render(): React.ReactElement {
    return (
      <Grid container className={DocumentationStyles.grid}>
        <Grid item xs={11} className={DocumentationStyles.gridItemFirst}>
          <h1>Documentation</h1>
          {this.getDocumentationHeader()}
        </Grid>
        {this.state.subDirectories.length > 0 ?
          <Grid item xs={11} className={DocumentationStyles.gridItem}>
            <h3>Folders</h3>
            <div className={DocumentationStyles.folderView}>
              {this.getFolders()}
            </div>
          </Grid>
          : ''
        }
        {this.state.files.length > 0 ?
          <Grid item xs={11} className={DocumentationStyles.gridItem}>
            <h3>Files</h3>
            {this.state.fileView ?
              <div className={DocumentationStyles.fileView}>
                {this.getFileView()}
              </div>
              :
              <div className={DocumentationStyles.listView}>
                {this.getListView()}
              </div>
            }
          </Grid>
          : ''
        }
      </Grid>
    );
  }
}